// ─── Dictate-and-send ────────────────────────────────────────────────────────
//
// "Tap a mic, speak, get editable transcribed text, send through the existing
// text pipeline" (V1 pivot, 2026-08-10). A text-input accelerator, not a voice
// channel — the transcript lands in the box the user was already typing in,
// and they can edit it before sending.
//
// Transcription happens server-side via Whisper rather than the browser's
// SpeechRecognition API, because that API is Chrome-family only and silently
// missing on iOS Safari. A steward on an iPhone getting no mic at all would be
// the whole feature failing for a large share of the field.

import { useRef, useState } from "react";

/**
 * A flat, 2D mic outline (Drea, 2026-08-17) replacing the 🎤 emoji, which
 * renders as a glossy 3D object on every platform and sits oddly against a
 * line-drawn interface. `currentColor` so it follows the ink token wherever it
 * is used, and `aria-hidden` because the button around it already carries the
 * label.
 */
function MicIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      aria-hidden width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
    >
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
      <path d="M8.5 21h7" />
    </svg>
  );
}

export type DictationState = "idle" | "recording" | "transcribing" | "unsupported" | "failed";

/**
 * Why dictation is unavailable, when it is.
 *
 * The distinction matters because one of these the person can fix in thirty
 * seconds and the other they cannot, and telling them the wrong one wastes
 * their afternoon.
 */
function unsupportedReason(): string | null {
  if (typeof navigator === "undefined") return "Dictation isn’t available here";
  // Browsers withhold the microphone APIs entirely on a non-secure origin —
  // `navigator.mediaDevices` is not merely restricted, it is UNDEFINED. Opening
  // the app over plain http on a LAN address is the ordinary way to hit this,
  // and it looks exactly like the feature was never built.
  if (!window.isSecureContext) {
    return "Dictation needs a secure (https) address — open the app over https and the mic comes back";
  }
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== "function") {
    return "This browser won’t give the page a microphone";
  }
  if (typeof MediaRecorder === "undefined") return "This browser can’t record audio";
  return null;
}

export function useDictation(onText: (text: string) => void) {
  const [reason] = useState<string | null>(() => unsupportedReason());
  const [state, setState] = useState<DictationState>(() => (unsupportedReason() ? "unsupported" : "idle"));
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);

  const start = async () => {
    if (state === "unsupported") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = e => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setState("transcribing");
        try {
          const blob = new Blob(chunks.current, { type: mr.mimeType || "audio/webm" });
          const res = await fetch("/api/dictate", {
            method: "POST",
            headers: { "Content-Type": blob.type },
            body: blob,
            credentials: "include",
          });
          if (!res.ok) throw new Error(String(res.status));
          const { text } = await res.json();
          if (text?.trim()) onText(text.trim());
          setState("idle");
        } catch {
          // Says so rather than silently producing nothing, which would read
          // as "it didn't hear you" and lose what was actually said.
          setState("failed");
        }
      };
      mr.start();
      recorder.current = mr;
      setState("recording");
    } catch {
      setState("unsupported");
    }
  };

  const stop = () => {
    recorder.current?.stop();
    recorder.current = null;
  };

  return { state, reason, start, stop, toggle: () => (state === "recording" ? stop() : start()) };
}

/**
 * The mic button.
 *
 * IT USED TO VANISH when the device couldn't record — "absent-by-degradation",
 * matching how TALK was described. Checked live on 2026-08-16 after Drea
 * reported seeing only TYPE and a greyed-out TALK: over plain http on a LAN
 * address `navigator.mediaDevices` is undefined, so the button removed itself
 * and the feature looked unbuilt. Same page over localhost showed it fine, so
 * this was never a stale deploy or a rendering bug.
 *
 * An absent control cannot explain itself. TALK greys out and says why, which
 * is exactly why she could see THAT one and report it. So this now does the
 * same: it stays, disabled, carrying the reason — and where the reason is a
 * non-secure origin, that is a thing the person can actually fix.
 */
export function DictateButton({ onText, label = "Dictate" }: { onText: (t: string) => void; label?: string }) {
  const { state, reason, toggle } = useDictation(onText);

  if (state === "unsupported") {
    return (
      <span
        role="note"
        aria-label={`Dictation unavailable: ${reason ?? "not supported here"}`}
        className="inline-flex items-center justify-center"
        style={{
          minWidth: "var(--nl-hit-min)", minHeight: "var(--nl-hit-min)",
          borderRadius: "var(--nl-radius-sm)",
          border: "1px dashed var(--nl-stroke)",
          color: "var(--nl-ink-faint)",
          cursor: "not-allowed",
        }}
        title={reason ?? "Dictation isn’t available on this device"}
      >
        <MicIcon />
      </span>
    );
  }

  const recording = state === "recording";
  const busy = state === "transcribing";
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-label={recording ? "Stop dictating" : label}
      aria-pressed={recording}
      className="inline-flex items-center justify-center"
      style={{
        minWidth: "var(--nl-hit-min)", minHeight: "var(--nl-hit-min)",
        borderRadius: "var(--nl-radius-sm)",
        background: recording ? "var(--nl-status-attention)" : "var(--nl-tile)",
        border: "1px solid var(--nl-stroke)",
        color: recording ? "#fff" : "var(--nl-ink)",
        cursor: busy ? "progress" : "pointer",
        opacity: busy ? 0.6 : 1,
      }}
      title={state === "failed" ? "Couldn’t transcribe — tap to try again" : label}
    >
      {busy ? "…" : recording ? "◼" : <MicIcon />}
    </button>
  );
}
